"use client";

import React, { useState } from "react";
import { useApp } from "./AppProvider";
import { submitQuoteAction } from "@/app/actions/quote";

interface QuoteFormProps {
  subject?: string;
}

export default function QuoteForm({ subject }: QuoteFormProps) {
  const { isRTL } = useApp();
  const [formData, setFormData] = useState({ name: "", email: "", description: "" });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    const result = await submitQuoteAction({
      ...formData,
      subject: subject || (isRTL ? "طلب استشارة عامة" : "General Inquiry"),
    });

    if (result.success) {
      setStatus("success");
      setFormData({ name: "", email: "", description: "" });
    } else {
      setStatus("error");
      setErrorMessage(result.error ?? "Something went wrong. Please try again.");
    }
  };

  const label = (en: string, ar: string) => (isRTL ? ar : en);

  if (status === "success") {
    return (
      <div className="glass-card" style={{ padding: "3rem", textAlign: "center", borderRadius: "24px" }}>
        <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>✅</div>
        <h3 style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--text-primary)", marginBottom: "1rem" }}>
          {label("Request Sent Successfully!", "تم إرسال طلبك بنجاح!")}
        </h3>
        <p style={{ color: "var(--text-muted)", lineHeight: 1.6 }}>
          {label(
            "Thank you for reaching out. Our team will review your request and get back to you shortly.",
            "شكراً لتواصلك معنا. سيقوم فريقنا بمراجعة طلبك والرد عليك في أقرب وقت ممكن."
          )}
        </p>
        <button
          onClick={() => setStatus("idle")}
          style={{
            marginTop: "2rem",
            padding: "0.8rem 2rem",
            background: "var(--gradient-brand)",
            color: "white",
            border: "none",
            borderRadius: "12px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {label("Send another request", "إرسال طلب آخر")}
        </button>
      </div>
    );
  }

  const descCharsLeft = 2000 - formData.description.length;

  return (
    <form onSubmit={handleSubmit} className="glass-card" style={{ padding: "2.5rem", borderRadius: "24px", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <h3 style={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--text-primary)", marginBottom: "0.5rem", textAlign: isRTL ? "right" : "left" }}>
        {label("Get a Professional Quote", "احصل على عرض سعر احترافي")}
      </h3>

      {subject && (
        <div style={{ padding: "0.8rem 1rem", background: "rgba(227,1,125,0.05)", borderRadius: "10px", border: "1px solid rgba(227,1,125,0.1)", fontSize: "0.85rem", color: "var(--color-pink)", fontWeight: 600, textAlign: isRTL ? "right" : "left" }}>
          {label("Interested in:", "مهتم بـ:")} {subject}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-secondary)", textAlign: isRTL ? "right" : "left" }}>
          {label("Full Name", "الاسم الكامل")}
        </label>
        <input
          type="text"
          required
          maxLength={100}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={label("Enter your name", "أدخل اسمك")}
          style={{ width: "100%", padding: "1rem", borderRadius: "12px", border: "1px solid var(--border-subtle)", background: "rgba(255,255,255,0.05)", color: "var(--text-primary)", outline: "none", textAlign: isRTL ? "right" : "left" }}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-secondary)", textAlign: isRTL ? "right" : "left" }}>
          {label("Email Address", "البريد الإلكتروني")}
        </label>
        <input
          type="email"
          required
          maxLength={254}
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder={label("example@email.com", "example@email.com")}
          style={{ width: "100%", padding: "1rem", borderRadius: "12px", border: "1px solid var(--border-subtle)", background: "rgba(255,255,255,0.05)", color: "var(--text-primary)", outline: "none", textAlign: "left" }}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-secondary)" }}>
            {label("Project Details", "تفاصيل المشروع")}
          </label>
          <span style={{ fontSize: "0.75rem", color: descCharsLeft < 100 ? "#ef4444" : "var(--text-muted)" }}>
            {descCharsLeft} {label("chars left", "حرف متبقي")}
          </span>
        </div>
        <textarea
          required
          rows={5}
          maxLength={2000}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder={label("Tell us about your project or inquiry...", "أخبرنا عن مشروعك أو استفسارك...")}
          style={{ width: "100%", padding: "1rem", borderRadius: "12px", border: "1px solid var(--border-subtle)", background: "rgba(255,255,255,0.05)", color: "var(--text-primary)", outline: "none", resize: "none", textAlign: isRTL ? "right" : "left" }}
        />
      </div>

      {status === "error" && (
        <div style={{ color: "#ef4444", fontSize: "0.85rem", fontWeight: 600, textAlign: isRTL ? "right" : "left" }}>
          ⚠️ {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        style={{
          width: "100%",
          padding: "1rem",
          background: "var(--gradient-brand)",
          color: "white",
          border: "none",
          borderRadius: "12px",
          fontWeight: 700,
          cursor: status === "loading" ? "not-allowed" : "pointer",
          boxShadow: "0 10px 20px rgba(227,1,125,0.2)",
          transition: "all 0.3s ease",
          opacity: status === "loading" ? 0.7 : 1,
        }}
      >
        {status === "loading" ? label("Sending...", "جاري الإرسال...") : label("Submit Request", "إرسال الطلب")}
      </button>
    </form>
  );
}
